import { useEffect, useState, useCallback, useRef } from "react";
import { useLazyGetCountryEmissionFactorsQuery } from "../app/features/other/genericApiSlice";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/hooks";
import { selectCurrentProject } from "../app/features/project/projectSlice";
import { getIn, useFormikContext } from "formik";
import { Country } from "../types/interfaces";

export const useMissingEmissionFactorForCountry = <T>({
  handleDefaultsError,
  listInputName
}: {
  handleDefaultsError: (message: string) => void,
  listInputName: string
}) => {
  const { t } = useTranslation();
  const { project } = useAppSelector(selectCurrentProject);
  const { values } = useFormikContext<T>();

  const [countries, setCountries] = useState<Country[]>([]);
  const [getCountryEmissionFactors] = useLazyGetCountryEmissionFactorsQuery();

  // Keep track of countries we've already checked
  const checkedCountryIds = useRef<Set<number>>(new Set());

  // Memoize this function to avoid recreating it on every render
  const handleMissingEmissionFactorForCountry = useCallback((country: Country) => {
    const errorKey = "validations.no_emission_factor_for_country";
    const defaultMessage = `No Operating Margin Emission Factor is available for ${country.name}. Please refine it below in the EF for selected Country section.`;
    const translatedError = t(errorKey, { countryName: country.name || 'the selected country' }) || defaultMessage;
    handleDefaultsError(translatedError);
  }, [t, handleDefaultsError]);

  // Extract and deduplicate countries from the list
  useEffect(() => {
    const list = getIn(values, listInputName);
    if (!list || !Array.isArray(list)) return;

    const countriesFromList: (Country | null)[] = list.map((item: any) => item.country_t2);
    const isAtLeastOneCountryNull = countriesFromList.some(country => country === null);

    // Deduplicate countries and filter out nulls
    const uniqueCountries = countriesFromList
      .filter((country): country is Country => !!country)
      .filter((country, index, self) => 
        self.findIndex(c => c && c.id === country.id) === index
      );

    // Include project country if any item has null country
    const projectCountry = isAtLeastOneCountryNull && project?.country ? [project.country] : [];

    // Filter out countries we've already checked
    const newCountries = [...uniqueCountries, ...projectCountry]
      .filter(country => country?.id && !checkedCountryIds.current.has(country?.id));

    setCountries(newCountries);
  }, [values, listInputName, project]);

  // Check emission factors for each country
  useEffect(() => {
    if (countries.length === 0) return;

    // Use Promise.all to handle all async operations together
    const checkEmissionFactors = async () => {
      try {
        await Promise.all(
          countries.map(async (country) => {
            if (!country.id) return;

            // Mark this country as checked
            checkedCountryIds.current.add(country?.id ?? 0);

            const result = await getCountryEmissionFactors(country.id);
            if (result.data && result.data.length === 0) {
              handleMissingEmissionFactorForCountry(country);
            }
          })
        );
      } catch (error) {
        console.error("Error checking emission factors:", error);
      }
    };

    checkEmissionFactors();
  }, [countries, getCountryEmissionFactors, handleMissingEmissionFactorForCountry]);
};
